import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Auth normally travels in HttpOnly cookies. When the app is embedded in a
// cross-site iframe (the Emergent preview) or the browser blocks third-party
// cookies, those cookies never stick — so we fall back to holding the access
// token in memory + sessionStorage (per tab, cleared on close) and sending it
// as a bearer header. Never localStorage.
const FALLBACK_KEY = "halo_fallback_token";
let fallbackToken = null;
try { fallbackToken = sessionStorage.getItem(FALLBACK_KEY); } catch { fallbackToken = null; }

export const hasSessionHint = () =>
  document.cookie.split("; ").some((c) => c.startsWith("halo_session="));

export const getFallbackToken = () => fallbackToken;

export const setFallbackToken = (token) => {
  fallbackToken = token || null;
  try {
    if (token) sessionStorage.setItem(FALLBACK_KEY, token);
    else sessionStorage.removeItem(FALLBACK_KEY);
  } catch { /* storage unavailable — memory copy still works for this page */ }
};

export const hasSession = () => hasSessionHint() || Boolean(fallbackToken);

const clearSessionHint = () => {
  document.cookie = "halo_session=; Max-Age=0; path=/";
  setFallbackToken(null);
};

export const api = axios.create({ baseURL: API_BASE, withCredentials: true });

api.interceptors.request.use((config) => {
  if (fallbackToken) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${fallbackToken}` };
  }
  return config;
});

const PUBLIC_PATHS = ["/login", "/welcome", "/forgot-password", "/reset-password", "/auth/google/callback"];

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const config = err?.config || {};
    const url = config.url || "";
    const onPublicPage = PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/signup") || url.includes("/auth/refresh");
    // The initial session probe failing just means "not signed in" — the router decides where to go.
    const isSessionProbe = url.includes("/auth/me");

    if (status === 401 && !isAuthCall && !config._retried && !(onPublicPage && isSessionProbe)) {
      try {
        await api.post("/auth/refresh");
        config._retried = true;
        return api.request(config);
      } catch {
        clearSessionHint();
        if (!onPublicPage && !isSessionProbe) window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const money = (n, currency = "AUD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 2 }).format(n || 0);

export const dateLong = (d) =>
  new Date(d).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

export const timeShort = (d) =>
  new Date(d).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
